import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CalibrationInfo, CalibrationItem, Person, Question } from 'types';
import { useProxyParameters } from 'utils/api/ApiHooks';
import { formatQuestion } from 'utils/custom-question/formatter';
import { stripHtml } from 'utils/util/util';
import ObjectsDropdown from 'views/Widgets/Dropdowns/ObjectsDropdown';
import { YES_NO_OPTIONS } from 'views/Widgets/Inputs/MultipleChoiceInput';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import { useCalibration } from './CalibrationContext';
import CalibrationHistoryItems from './CalibrationHistoryItems';

type FormObject = {
  value?: unknown;
  comment?: string;
};

interface Props {
  question: Question;
  calibrationInfo: CalibrationInfo;
  targetPerson: Person;
  editable?: boolean;
}

const INVERSE_LIKERT_SCALE = [
  { id: 5, name: 5 },
  { id: 4, name: 4 },
  { id: 3, name: 3 },
  { id: 2, name: 2 },
  { id: 1, name: 1 },
];

const TeamDashboardCalibrationEditor: FC<Props> = ({
  question,
  calibrationInfo,
  targetPerson,
  editable = false,
}) => {
  const { formatMessage } = useIntl();
  const { setCalibrationItems, calibrationViewId } = useCalibration();
  const [isOpen, setIsOpen] = React.useState(false);
  const {
    calibrationItems,
    currentValue,
    priorityCurrentValue,
    originalResponses,
    surveyResponseId,
  } = calibrationInfo;

  const value = useMemo(() => {
    return priorityCurrentValue ?? currentValue;
  }, [priorityCurrentValue, currentValue]);

  const onSubmitCallback = useCallback(
    (savedCalibrationItem: CalibrationItem) => {
      if (savedCalibrationItem) {
        setCalibrationItems((prevCalibrationItems: CalibrationItem[]) => [
          ...prevCalibrationItems,
          {
            ...savedCalibrationItem,
            survey_response: surveyResponseId,
          },
        ]);
      }
      setIsOpen(false);
    },

    [surveyResponseId, setCalibrationItems]
  );

  const proxyParameters = useProxyParameters();

  const transformObjectBeforeSubmit = useCallback(
    (obj: FormObject) => {
      return {
        ...proxyParameters,
        key: question.key,
        value: obj.value,
        comment: obj.comment,
        survey_response: calibrationInfo.surveyResponseId,
      };
    },
    [question, proxyParameters, calibrationInfo]
  );

  const renderInputs = useCallback(
    ([value, comment]) => (
      <>
        {value}
        {comment}
        <hr />

        <CalibrationHistoryItems
          calibrationItems={calibrationItems}
          question={question}
          originalResponses={originalResponses}
        />
      </>
    ),
    [originalResponses, question, calibrationItems]
  );

  const handleOnValidate = useCallback(
    (obj: FormObject) => {
      // Require a comment if the value has changed
      if (obj.value === value && !stripHtml(obj.comment)?.trim()) {
        return {
          comment: formatMessage({
            id: 'app.views.widgets.dashboards.team_dashboard.team_dashboard_calibration_editor.validation.comment_required',
            defaultMessage:
              'If you want to confirm the same answer, please provide a reason.',
          }),
        };
      }
      return {};
    },
    [value, formatMessage]
  );

  const objects = useMemo(() => {
    return (
      // @ts-expect-error
      (question.scale ? INVERSE_LIKERT_SCALE : undefined) ??
      // @ts-expect-error
      question.options ??
      // @ts-expect-error
      question.objects ??
      (question.type === INPUT_TYPES.YES_NO
        ? YES_NO_OPTIONS(formatMessage)
        : undefined) ??
      []
    );
  }, [question, formatMessage]);

  const [finalRating, setFinalRating] = React.useState(() => currentValue);

  const onUpdateFinalRating = useCallback((e) => {
    setFinalRating(e.target.value);
    setIsOpen(true);
  }, []);

  return (
    <>
      {editable && (
        <ObjectsDropdown
          onlyAllowProgrammaticChanges={true}
          buttonClassName="btn-sm"
          selectText="?"
          objects={objects}
          container="body"
          // @ts-expect-error
          value={value}
          onChange={onUpdateFinalRating}
        />
      )}
      {!editable && (
        <span>
          {formatQuestion(question, value, formatMessage, { numeric: true })}
        </span>
      )}
      {editable && isOpen && (
        <ModalEditor
          isOpen={isOpen}
          toggle={() => setIsOpen(!isOpen)}
          url={`calibration-items${
            calibrationViewId ? '?calibrationViewId=' + calibrationViewId : ''
          }`}
          title={formatMessage(
            {
              id: 'app.views.widgets.dashboards.team_dashboard.team_dashboard_calibration_editor.modal.title',
              defaultMessage: 'Update value for {personFullName}',
            },
            { personFullName: targetPerson.full_name }
          )}
          submitText={formatMessage({
            id: 'app.views.widgets.dashboards.team_dashboard.team_dashboard_calibration_editor.submit.text',
            defaultMessage: 'Save',
          })}
          object={{
            value: finalRating,
          }}
          keepOpenOnSuccess={true}
          onValidate={handleOnValidate}
          callback={onSubmitCallback}
          inputs={[
            {
              ...question,
              ...(question?.type === INPUT_TYPES.LIKERT
                ? {
                    objects: [
                      {
                        // @ts-expect-error
                        value: question?.label,
                        name: question.key.substring(
                          question.key.indexOf('_id_') + 1
                        ),
                      },
                    ],
                  }
                : {}),
              name: 'value',
              labelClassName: 'pb-4',
            },
            {
              name: 'comment',
              type: INPUT_TYPES.RICH_TEXT_EDITOR,
              config: { charCounterMax: 1000 },
              minRows: 2,
              label: formatMessage({
                id: 'app.views.widgets.dashboards.team_dashboard.team_dashboard_calibration_editor.comment.label',
                defaultMessage:
                  'Why do you believe this new rating is appropriate?',
              }),
              helperText: formatMessage({
                id: 'app.views.widgets.dashboards.team_dashboard.team_dashboard_calibration_editor.comment.help_text',
                defaultMessage:
                  'Add a comment to explain the reason for this calibration (optional).',
              }),
            },
          ]}
          transformObjectBeforeSubmit={transformObjectBeforeSubmit}
          renderInputs={renderInputs}
        />
      )}
    </>
  );
};

export default React.memo(TeamDashboardCalibrationEditor);
